import { LoadingButton } from "@mui/lab";
import {
  Box,
  Stack,
  TextField,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
  Alert,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import ChipRadioGroup from "../common/RadioChip/RadioChip";
import "./styles.scss";
import { createAccount, editAccount } from "../../services/accountService";
import NumericField from "../common/NumericField/NumericField";
import { AccountType } from "../../types/genericType";
import { formatDateToISOString, formatDate } from "../../utils/dateFormat";
import AutoCompleteSelect from "../common/autocompleteSelect/AutoCompleteSelect";
import { formatDollar } from "../../utils/utils";
import dayjs, { Dayjs } from "dayjs";
import { convertToLocal } from "../../utils/dateFormat";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { getLatestBook } from "../../services/booksService";

type NewAccountProps = {
  setDrawerOpen: Function;
  clientOptions: any[];
  setOnTxnSuccess: Function;
  setSnackbarMsg: Function;
  getAccountsData: Function;
  editFlow?: boolean;
  selectedAccount: AccountType;
  currentAccountStatus?: boolean;
  updatedAccountStatus?: boolean;
  handleDrawerClose: Function;
  clientView: boolean;
  updateSelectedAccount: Function;
  setEditFlow: Function;
};

const NewAccount = (props: NewAccountProps) => {
  const {
    setDrawerOpen,
    clientOptions,
    setOnTxnSuccess,
    setSnackbarMsg,
    getAccountsData,
    editFlow,
    selectedAccount,
    currentAccountStatus,
    updatedAccountStatus,
    handleDrawerClose,
    clientView,
    updateSelectedAccount,
    setEditFlow,
  } = props;
  const [investor, setInvestor] = useState<string>(
    editFlow ? selectedAccount?.investor.toString() : ""
  );
  
  const [accountName, setAccountName] = useState<string>(
    editFlow ? selectedAccount?.name : ""
  );
  const [name1099, setName1099] = useState<string>(
    selectedAccount?.report1099Name ?? ""
  );
  const [accountType, setAccountType] = useState<string>(
    editFlow ? selectedAccount?.accountType : "Investment"
  );
  const [rate, setRate] = useState<string>(
    editFlow ? selectedAccount?.rate?.toString() || "" : ""
  );

  const [email, setEmail] = useState<string>(selectedAccount?.email || "");
  const [sendEmail, setSendEmail] = useState<boolean>(
    selectedAccount?.sendEmail || false
  );
  const [sendMail, setSendMail] = useState<boolean>(
    selectedAccount?.sendMail || false
  );

  const [interestType, setInterestType] = useState<string>(
    editFlow ? selectedAccount?.interestType : "Accrual"
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  const [taxNumber, setTaxNumber] = useState<string>(selectedAccount?.taxNumber || "");

  const maskTaxNumber = (taxNum: string): string => {
    if (!taxNum) return "";
    
    // Remove all non-digit characters to get the raw number
    const digitsOnly = taxNum.replace(/\D/g, "");
    
    // SSN format: XXX-XX-XXXX (9 digits, pattern 3-2-4)
    // TIN format: XX-XXXXXXX (9 digits, pattern 2-7)
    
    if (digitsOnly.length === 9) {
      // Check if it matches SSN format (has dashes in XXX-XX-XXXX pattern)
      if (taxNum.includes("-") && taxNum.match(/^\d{3}-\d{2}-\d{4}$/)) {
        // SSN format: XXX-XX-1234
        const last4 = digitsOnly.slice(-4);
        return `XXX-XX-${last4}`;
      } else if (taxNum.includes("-") && taxNum.match(/^\d{2}-\d{7}$/)) {
        // TIN format: XX-XXX1234
        const last4 = digitsOnly.slice(-4);
        return `XX-XXX${last4}`;
      } else {
        // If no dashes, try to determine by length patterns
        // Assume SSN if we can't determine (most common)
        const last4 = digitsOnly.slice(-4);
        return `XXX-XX-${last4}`;
      }
    }
    
    // If not 9 digits, just mask the last 4
    if (digitsOnly.length > 4) {
      const last4 = digitsOnly.slice(-4);
      const masked = "X".repeat(digitsOnly.length - 4);
      return masked + last4;
    }
    
    return taxNum;
  };

  const [startDate, setStartDate] = useState<Dayjs | null>(
    selectedAccount?.startDate && selectedAccount?.startDate !== ""
      ? convertToLocal(dayjs(selectedAccount?.startDate))
      : dayjs(new Date())
  );

  const [accountDatePickerMinDate, setAccountDatePickerMinDate] = useState<Dayjs>(dayjs(new Date().setDate(new Date().getDate() - 30)));
  const [accountDatePickerMaxDate, setAccountDatePickerMaxDate] = useState<Dayjs>(dayjs(new Date().setDate(new Date().getDate() + 30)));  
  

  const setAccountDatePickerMinAndMaxDates = async () => {
    getLatestBook()
      ?.then((res) => {
        const currentDate = dayjs();
        const lastMonthDate = currentDate.subtract(1, "month");
        const nextMonthDate = currentDate.add(1, "month");
  
        const latestMonthEndDate = dayjs(new Date(res.year, res.month, 1));
          
        setAccountDatePickerMinDate(latestMonthEndDate.isBefore(lastMonthDate) ? latestMonthEndDate : lastMonthDate);
        setAccountDatePickerMaxDate(nextMonthDate);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  useEffect(() => {
    if (accountName) {
      setError("");
    }
  }, [accountName]);

  useEffect(() => {
    setAccountDatePickerMinAndMaxDates();
  }, []);

  const handleCreateAccount = () => {
    if (accountName === "") {
      setError("Please fill all the required fields");
      // if rate is not defined or %0, then show error
    } else if (rate === "0" || rate === "0%") {
      setError("Rate cannot be 0");
    } else {
      setIsLoading(true);
      createAccount(
        investor,
        accountName,
        name1099,
        accountType,
        accountType === "Investment" ? interestType : null,
        rate === "0%" ? null : parseFloat(rate),
        startDate ? startDate.toISOString() : new Date().toISOString()
      )
        .then((res) => {
          console.log("res", res);
          // handleDrawerClose();
          // setOnTxnSuccess(true);
          setSnackbarMsg("Account created successfully");
          updateSelectedAccount(res);
          setIsLoading(false);
          setEditFlow(true);
          // setDrawerOpen(false);
          getAccountsData();
        })
        .catch(() => {
          setOnTxnSuccess(false);
          setSnackbarMsg("Error creating account");
          setIsLoading(false);
        });
    }
  };

  const updatedEndDate = () => {
    if (currentAccountStatus === updatedAccountStatus) {
      return selectedAccount?.endDate;
    } else if (updatedAccountStatus === false) {
      return formatDateToISOString(Date.now());
    } else {
      return null;
    }
  };

  const handleEditAccount = () => {
    if (accountName === "") {
      setError("Please fill all the required fields");
    } else if (rate === "0" || rate === "0%") {
      setError("Rate cannot be 0");
    } else {
      setIsLoading(true);
      editAccount(
        selectedAccount?.id,
        investor,
        accountName,
        name1099,
        accountType,
        accountType === "Investment" ? interestType : null,
        rate === "0%" ? null : parseFloat(rate),
        selectedAccount?.startDate,
        updatedEndDate(),
        sendMail,
        sendEmail,
        email,
        taxNumber
      )
        .then(() => {
          handleDrawerClose();
          setOnTxnSuccess(true);
          getAccountsData();
          setSnackbarMsg("Account updated successfully");
          setIsLoading(false);
          setDrawerOpen(false);
        })
        .catch(() => {
          setOnTxnSuccess(false);
          setSnackbarMsg("Error updating account");
          setIsLoading(false);
        });
    }
  };

  return (
    <Box className="new-transaction">
      <Stack spacing={3}>
        {editFlow && (
          <Box className="account-box">
            <Typography className="account-title">Account Balance:</Typography>
            <Typography className="account-balance">{`${formatDollar(
              selectedAccount?.balance
            )}`}</Typography>
          </Box>
        )}
        {clientView ? (
          <TextField
            id="outlined-helperText"
            label="Client"
            value={selectedAccount?.investorName}
            size="small"
            disabled={editFlow}
            fullWidth
          />
        ) : (
          <AutoCompleteSelect
            items={clientOptions}
            onSelectItem={setInvestor}
            selected={investor?.toString() ?? ""}
            placeholder="Select an client"
            label="Client"
            required
          />
        )}
        {investor && (
          <>
            <TextField
              id="outlined-helperText"
              label="Account Name"
              required
              size="small"
              fullWidth
              value={accountName}
              onChange={(event) => setAccountName(event.target.value as string)}
              disabled={clientView}
            />
            <TextField
              id="outlined-helperText"
              label="1099 Name"
              size="small"
              fullWidth
              value={name1099}
              onChange={(event) => setName1099(event.target.value as string)}
              disabled={clientView}
            />
            <Box className="date-picker-container">
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Account Opening Date"
                  className="date-picker-container"
                  slotProps={{ textField: { size: "small" } }}
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  minDate={accountDatePickerMinDate}
                  maxDate={accountDatePickerMaxDate}
                  disabled={clientView || editFlow}
                  sx={{
                    "& .Mui-selected": {
                      color: "#FFFFFF",
                      borderRadius: 2,
                      borderWidth: 1,
                      borderColor: "#e91e63",
                      border: "1px solid",
                      backgroundColor: "#f8bbd0",
                    },
                  }}
                />
              </LocalizationProvider>
            </Box>            
            <ChipRadioGroup
              label="Account Type"
              value={accountType}
              setValue={setAccountType}
              options={["Investment", "Loan"]}
              disabled={editFlow || clientView}
            />
            {accountType === "Investment" ? (
              <ChipRadioGroup
                label="Interest Type"
                value={interestType}
                setValue={setInterestType}
                options={["Accrual", "Check", "ACH"]}
                disabled={clientView}
              />
            ) : null}
            <NumericField
              label="Rate"
              val={rate}
              setVal={setRate}
              testId="percent-owned"
              decimalPlaces={5}
              placeholder="0.00%"
              endsWith="%"
              disabled={clientView}
            />
            {error && (
              <Typography variant="sFormHelper" className="error-text">
                {error}
              </Typography>
            )}

            <TextField
              id="outlined-helperText"
              onChange={(event) => setTaxNumber(event.target.value as string)}
              value={clientView ? maskTaxNumber(taxNumber) : taxNumber}
              autoComplete="off"
              label="Tax Number"
              variant="outlined"
              fullWidth
              size="small"
              disabled={clientView}
            />

            <TextField
              id="outlined-error-helper-text"
              type="email"
              inputProps={{ "data-testid": "emailId" }}
              onChange={(event) => setEmail(event.target.value as string)}
              value={email}
              autoComplete="off"
              label="Additional Account Email Recipient"
              variant="outlined"
              fullWidth
              size="small"
              disabled={clientView}
            />

            <Stack direction="row" spacing={2} className="statements-header">
              <Typography variant="subtitle1" className="subtitle">
                Send statements to:{" "}
              </Typography>
              <Typography variant="subtitle1" className="subtitle1">
                Email
              </Typography>
              <Typography variant="subtitle1" className="subtitle1">
                Mail
              </Typography>
            </Stack>

            <Stack direction="row" spacing={2} className="statements-data">
              <Typography className="subtitle">
                {clientOptions.find((client) => client.value === investor)
                  ?.label || selectedAccount.investorName}
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={sendEmail}
                    onChange={() => setSendEmail(!sendEmail)}
                    disabled={clientView}
                  />
                }
                label=""
                className="fs24"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={sendMail}
                    onChange={() => setSendMail(!sendMail)}
                    disabled={clientView}
                  />
                }
                label=""
                className="mail"
              />
            </Stack>
            {selectedAccount?.accountNumber && (
              <>
                <Alert severity="success" icon={false}>
                  Your Account number is: {selectedAccount?.accountNumber}
                </Alert>

                <Stack
                  spacing={2}
                  direction="row"
                  justifyContent="space-between"
                >
                  <Alert
                    severity="warning"
                    icon={false}
                    style={{ width: "48%" }}
                  >
                    <strong>Open Date:</strong>
                    <br />
                    {selectedAccount?.startDate
                      ? formatDate(new Date(selectedAccount?.startDate))
                      : "N/A"}
                  </Alert>
                  <Alert
                    severity="warning"
                    icon={false}
                    style={{ width: "48%" }}
                  >
                    <strong>Close Date:</strong>
                    <br />
                    {selectedAccount?.endDate
                      ? formatDate(new Date(selectedAccount?.endDate))
                      : "N/A"}
                  </Alert>
                </Stack>
              </>
            )}

            {!clientView && (
              <Stack direction="row" spacing={2}>
                <LoadingButton
                  variant="contained"
                  data-testid="createTxnBtn"
                  size="small"
                  className="create-button"
                  onClick={editFlow ? handleEditAccount : handleCreateAccount}
                  loading={isLoading}
                >
                  {editFlow ? "SAVE CHANGES" : "Create Account"}
                </LoadingButton>
                <Button
                  className="cancel-button"
                  onClick={() => handleDrawerClose()}
                >
                  CANCEL
                </Button>
              </Stack>
            )}
          </>
        )}
      </Stack>
    </Box>
  );
};

export default NewAccount;
